/* eslint-disable */
import { useState, useMemo, useEffect } from "react";
import type { MessageDescriptor } from "react-intl";
import { usePasswordValidator } from "../..";
import { PasswordFieldProps, UsePasswordState, UsePasswordActions, UsePasswordType, UsePasswordValue } from "./types";
import messages from "./messages";
import { useUserSettings } from "../../../modules/user/components/UserSettings/useUserSettings";
import { RequestStatus } from "../../../modules/auth";

export function usePasswordField(props: PasswordFieldProps): [UsePasswordState, UsePasswordActions] {
  const [type, setType] = useState<UsePasswordType>("password");
  const [label, setLabel] = useState<MessageDescriptor>(messages.currentPassword);
  const [showError, setShowError] = useState<boolean>(false);
  let [valid, errors] = usePasswordValidator(props.value as UsePasswordValue, props.useMatch);
  const { applied, status } = useUserSettings();

  if (
    (props.mode === "confirmPassword" && !!props.newPassword && props.newPassword !== props.value) ||
    (props.mode === "newPassword" && !!props.confirmPassword && props.confirmPassword !== props.value)
  ) {
    errors.push(messages.confirmPasswordNoEqualError);
    valid = false;
  } else if (props.mode === "newPassword" && !!props.currentPassword && props.currentPassword === props.value) {
    errors.push(messages.passwordsIdentityError);
    valid = false;
  }

  useEffect(() => {
    if (applied) {
      setShowError(false);
    }
    if (status === RequestStatus.Fail){
      setShowError(false);
    }
  }, [applied, status]);

  useEffect(() => {
    let msg = messages.currentPassword;
    switch (props.mode) {
      case "newPassword":
        msg = messages.newPassword;
        break;
      case "confirmPassword":
        msg = messages.confirmPassword;
        break;
    }
    setLabel(msg);
  }, [props]);

  useEffect(() => {
    if (!valid && props.onPasswordInvalid) {
      props.onPasswordInvalid(errors);
    }
    if (valid && props.onPasswordValid) {
      props.onPasswordValid();
    }
  }, [valid, props]);

  const onChange = useMemo(
    () => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (props.onChange) {
        props.onChange(event);

        if (
          props.mode === "confirmPassword" &&
          !!event.target.value.length &&
          !!props.newPassword &&
          props.newPassword !== event.target.value
        ) {
          setShowError(true);
        } else if (
          props.mode === "newPassword" &&
          !!event.target.value.length &&
          !!props.confirmPassword &&
          props.confirmPassword !== event.target.value
        ) {
          setShowError(true);
        } else if (valid) {
          setShowError(false);
        } else {
          setShowError(true);
        }
      }
    },
    [props]
  );

  const onFocus = useMemo(
    () => (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (props.onFocus) {
        props.onFocus(event);
      }
    },
    [props]
  );

  const toggleShowType = useMemo(
    () => () => {
      setType(type === "text" ? "password" : "text");
    },
    [type]
  );

  return [
    {
      type,
      showError,
      valid,
      errors,
      label,
    },
    {
      toggleShowType,
      onChange,
      onFocus,
    },
  ];
}
